import {IllustratedMessage} from '@ui/images/illustrated-message';
import {Trans} from '@ui/i18n/trans';
import {TrackTable} from '@app/web-player/tracks/track-table/track-table';
import React, { Fragment, useMemo } from 'react';
import {queueGroupId} from '@app/web-player/queue-group-id';
import {useAlbum} from '@app/web-player/albums/requests/use-album';
import {
    actionButtonClassName,
    MediaPageHeaderLayout,
} from '@app/web-player/layout/media-page-header-layout';
import {AvatarGroup} from '@ui/avatar/avatar-group';
import {Avatar} from '@ui/avatar/avatar';
import {FormattedDuration} from '@ui/i18n/formatted-duration';
import {PlaybackToggleButton} from '@app/web-player/playable-item/playback-toggle-button';
import {AlbumImage} from '@app/web-player/albums/album-image/album-image';
import {Album} from '@app/web-player/albums/album';
import {getSmallArtistImage} from '@app/web-player/artists/artist-image/small-artist-image';
import {getArtistLink} from '@app/web-player/artists/artist-link';
import {FormattedDate} from '@ui/i18n/formatted-date';
import {useSortableTableData} from '@common/ui/tables/use-sortable-table-data';
import {BulletSeparatedItems} from '@app/web-player/layout/bullet-separated-items';
import {CommentList} from '@common/comments/comment-list/comment-list';
import {useAlbumPermissions} from '@app/web-player/albums/use-album-permissions';
import {PageStatus} from '@common/http/page-status';
import {PageMetaTags} from '@common/http/page-meta-tags';
import {TrackActionsBar} from '@app/web-player/tracks/track-actions-bar';
import {FocusScope} from '@react-aria/focus';
import {ChipList} from '@ui/forms/input-field/chip-field/chip-list';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import {Link} from 'react-router';
import {CommentBarContextProvider} from '@app/web-player/tracks/waveform/comment-bar-context';
import {CommentBarNewCommentForm} from '@app/web-player/tracks/waveform/comment-bar-new-comment-form';
import {AdHost} from '@common/admin/ads/ad-host';
import {useCommentPermissions} from '@app/web-player/tracks/hooks/use-comment-permissions';
import {TruncatedDescription} from '@common/ui/other/truncated-description';

// import { AlbumPageTabs } from '@player/web-player/albums/album-page-tabs';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@common/http/query-client';

import { AlbumListItem } from '@player/web-player/albums/album-list/album-list-item';
import { FilterOperator } from '@common/datatable/filters/backend-filter';

import {encodeBackendFilters, FilterListValue} from '@common/datatable/filters/utils/encode-backend-filters';
import { BackendFiltersUrlKey } from '@common/datatable/filters/backend-filters-url-key';

import { ArtistGridItem } from '@app/web-player/artists/artist-grid-item';
import { ContentGrid } from '@app/web-player/playable-item/content-grid';
import {Artist} from '@app/web-player/artists/artist';

export function AlbumPage() {
    const {canView: showComments, canCreate: allowCommenting} =
        useCommentPermissions();
    const query = useAlbum({loader: 'albumPage'});
    const {canEdit} = useAlbumPermissions(query.data?.album);

    if (query.data) {
        return (
            <div>
                <CommentBarContextProvider>
                    <PageMetaTags query={query} />
                    <AdHost slot="general_top" className="mb-44" />
                    <AlbumPageHeader album={query.data.album} />
                    {allowCommenting ? (
                        <CommentBarNewCommentForm
                            className="mb-16 z-50 relative"
                            commentable={query.data.album}
                        />
                    ) : null}
                </CommentBarContextProvider>
                {query.data.album.tags?.length ? (
                    <FocusScope>
                        <ChipList className="mb-16 relative z-50" selectable>
                            {query.data.album.tags.map(tag => (
                                <Chip elementType={Link} to={`/tag/${tag.name}`} key={tag.id}>
                                    #{tag.display_name || tag.name}
                                </Chip>
                            ))}
                        </ChipList>
                    </FocusScope>
                ) : null}
                <TruncatedDescription
                    description={query.data.album.description}
                    className="mt-24 text-sm relative z-50"
                />
                <AdHost slot="album_above" className="mt-34 relative z-50" />
                {/*<AlbumPageTabs />*/}
                <AlbumTrackTable album={query.data.album} />

                <RelatedAlbums
                    albumID={query.data.album.id}
                    tags={query.data.album.tags || []}
                />

                <RelatedArtists
                    genres={query.data.album.genres}
                />

                {showComments && (
                    <CommentList
                        className="mt-34 relative z-50"
                        commentable={query.data.album}
                        canDeleteAllComments={canEdit}
                    />
                )}
                <AdHost slot="general_bottom" className="mt-44 relative z-50" />
            </div>
        );
    }

    return (
        <PageStatus
            query={query}
            loaderClassName="absolute inset-0 m-auto"
            loaderIsScreen={false}
        />
    );
}

interface AlbumTrackTableProps {
    album: Album;
}
function AlbumTrackTable({album}: AlbumTrackTableProps) {
    const {data, sortDescriptor, onSortChange} = useSortableTableData(
        album.tracks,
    );
    return (
        <div className="mt-44">
            <div className="block relative font-semibold z-50 mb-12"><Trans message="Tracks" /></div>

            <TrackTable
                queueGroupId={queueGroupId(album)}
                tracks={data}
                sortDescriptor={sortDescriptor}
                onSortChange={onSortChange}
                hideArtist
                hideAlbum
                hidePopularity={false}
            />
            {!album.tracks?.length ? (
                <IllustratedMessage
                    className="mt-34"
                    title={<Trans message="Nothing to display" />}
                    description={
                        <Trans message="This album does not have any tracks yet" />
                    }
                />
            ) : null}
        </div>
    );
}

interface PlaylistPageHeaderProps {
    album: Album;
}
function AlbumPageHeader({album}: PlaylistPageHeaderProps) {
    const totalDuration = album.tracks?.reduce(
        (t, track) => t + (track.duration || 0),
        0,
    );

    return (
        <Fragment>
            <div className="top-0 right-0 z-10 absolute left-0">
                <span className="bg-gradient-to-b from-background/80 to-background absolute inset-0 z-50" />
                <AlbumImage album={album} className="w-full h-auto max-h-400 object-top" />
            </div>

            <MediaPageHeaderLayout
                className="mb-28 z-50 relative overflow-hidden"
                image={<AlbumImage album={album} className="rounded" />}
                title={album.name}
                subtitle={
                    <AvatarGroup>
                        {album.artists?.map(artist => (
                            <Avatar
                                key={artist.id}
                                circle
                                src={getSmallArtistImage(artist)}
                                label={artist.name}
                                link={getArtistLink(artist)}
                            />
                        ))}
                    </AvatarGroup>
                }
                description={
                    <BulletSeparatedItems className="text-sm text-muted">
                        {album.tracks?.length ? (
                            <Trans
                                message="[one 1 track|other :count tracks]"
                                values={{count: album.tracks.length}}
                            />
                        ) : null}
                        {album.tracks?.length ? (
                            <FormattedDuration ms={totalDuration} verbose />
                        ) : null}
                        <FormattedDate date={album.release_date} />
                    </BulletSeparatedItems>
                }
                actionButtons={
                    <TrackActionsBar
                        item={album}
                        managesItem={false}
                        buttonGap={undefined}
                        buttonSize="sm"
                        buttonRadius="rounded-full"
                        buttonClassName={actionButtonClassName()}
                    >
                        <PlaybackToggleButton
                            disabled={!album.tracks?.length}
                            buttonType="text"
                            queueId={queueGroupId(album)}
                            className={actionButtonClassName({isFirst: true})}
                        />
                    </TrackActionsBar>
                }
            />
        </Fragment>
    );
}

function RelatedAlbums({ albumID, tags }: { albumID: number, tags: Album['tags'] }) {
    const params = useMemo(() => {
        const filters: FilterListValue[] = [
            {
                key: 'id',
                operator: FilterOperator.ne,
                value: albumID
            }
        ]

        if (tags && tags?.length > 0) {
            filters.push({
                key: 'tags',
                operator: FilterOperator.has,
                value: tags?.map(tag => ({
                    id: tag.id,
                    // name: tag.name,
                    // display_name: tag.display_name
                }))
            })
        }

        return {
            [BackendFiltersUrlKey]: encodeBackendFilters(filters),
            with: 'tracks,artists,genres,tags,tracks.artists,tracks.activity',
            withCount: 'tracks',
            perPage: 10,
            order: 'release_date:desc'
        }
    }, [albumID, tags]);
    const query = useQuery({
        queryKey: ['relatedAlbums', params],
        queryFn: async () => {

            return apiClient.get('albums', {
                params
            }).then((res) => res.data)
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        retry: false,
    })

    return (
        <div className="mt-44">
            <div className="block relative font-semibold z-50 mb-24"><Trans message="Related albums" /></div>

            <div className="space-y-52">
                {query?.isLoading ? (
                    <>
                        loading ...
                    </>
                ) : query?.data?.pagination?.data?.map((album: Album) => (
                    <AlbumListItem
                        key={album.id}
                        album={album}
                        className="mb-12"
                    />
                ))}
            </div>
        </div>
    )
}

function RelatedArtists({ genres }: { genres: Album['genres'] }) {
    const params = useMemo(() => {
        return {
            genre_ids: genres?.map(genre => genre.id).join(','),
            order: 'release_date:desc'
        }
    }, [genres]);
    const query = useQuery({
        queryKey: ['relatedArtists', params],
        queryFn: async () => {
            return apiClient.get('artists/related', {
                params
            }).then((res) => res.data)
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        retry: false,
    })

    return (
        <div className="mt-44">
            <div className="block relative font-semibold z-50 mb-24"><Trans message="Related artists" /></div>

            { query?.isLoading ? (
                <>
                    loading...
                </>
            ) : query?.data?.artists?.length && (
                <ContentGrid>
                    {query?.data?.artists?.map((artists: Artist) => (
                        <ArtistGridItem
                            artist={artists}
                            key={artists.id}
                        />
                    ))}
                </ContentGrid>
            )}
        </div>
    )
}
